import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Er ging iets mis — probeer het opnieuw");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white antialiased"
      data-testid="forgot-password-page"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
    >
      <div className="relative flex flex-col px-6 py-8 lg:px-12 lg:py-10 bg-white">
        <div
          className="text-slate-900 text-base tracking-tight"
          style={{ fontFamily: 'Figtree, Inter, ui-sans-serif, system-ui, sans-serif', fontWeight: 500 }}
        >
          The Brand Hub
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                <Mail className="w-5 h-5 text-slate-500" strokeWidth={1.75} />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
                {sent ? "Check je mail" : "Wachtwoord vergeten?"}
              </h1>
              <p className="text-sm text-slate-500 mt-1.5 text-center">
                {sent
                  ? `Als er een account bestaat voor ${email}, hebben we een reset-link verstuurd. De link is 30 minuten geldig.`
                  : "Vul je e-mailadres in. We sturen je een link om je wachtwoord opnieuw in te stellen."}
              </p>
              {sent && (
                <p className="text-xs text-slate-500 mt-3 text-center max-w-xs">
                  <strong className="text-slate-700">Niets ontvangen?</strong> Check je <strong>spam / ongewenste e-mail</strong> map
                  — e-mails van <em>no-reply@mail.emergentagent.com</em> belanden daar soms. Vraag anders een nieuwe link aan.
                </p>
              )}
            </div>

            {!sent && (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">E-mail</Label>
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0 focus-visible:border-slate-900"
                  />
                </div>
                <Button
                  data-testid="forgot-submit"
                  disabled={busy}
                  type="submit"
                  className="w-full h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 shadow-none text-sm font-medium disabled:opacity-60"
                >
                  {busy ? "Versturen…" : "Verstuur reset-link"}
                </Button>
              </form>
            )}

            {sent && (
              <Button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-send-again"
                className="mt-4 w-full h-10 rounded-md bg-white border border-slate-200 text-slate-900 hover:bg-slate-50 shadow-none text-sm font-medium"
              >
                Nog een link aanvragen
              </Button>
            )}

            <button
              onClick={() => navigate("/login")}
              data-testid="back-to-login"
              className="mt-6 w-full inline-flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Terug naar inloggen
            </button>
          </div>
        </div>
      </div>

      <div className="hidden lg:block" style={{ backgroundColor: "#b8cff8" }} aria-hidden="true" />
    </div>
  );
}
